import React, { useEffect, useState, useMemo } from 'react';
import { db, auth } from '../services/firebase';
import { collection, query, where, onSnapshot, orderBy, doc, getDoc, updateDoc, deleteDoc, addDoc, serverTimestamp } from 'firebase/firestore';
import {
  Typography, Card, CardContent, Box, List, ListItem, ListItemText, IconButton, Grid, Select, MenuItem, FormControl, InputLabel, Button,
  Dialog, DialogActions, DialogContent, DialogTitle, TextField
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import styles from '../styles/Dashboard.module.css';
import { format, intervalToDuration } from 'date-fns';

const GerenciarPonto = () => {
  const [funcionarios, setFuncionarios] = useState([]);
  const [registros, setRegistros] = useState([]);
  const [condominioId, setCondominioId] = useState('');
  
  // Filtros
  const [filtroFuncionario, setFiltroFuncionario] = useState('');
  const [filtroDataInicio, setFiltroDataInicio] = useState(null);
  const [filtroDataFim, setFiltroDataFim] = useState(null);

  // Modal de Edição
  const [editDialog, setEditDialog] = useState(false);
  const [selectedRegistro, setSelectedRegistro] = useState(null);
  const [editFormData, setEditFormData] = useState({ timestamp: null, comentario: '' });

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(async (user) => {
      if (user) {
        const userDocRef = doc(db, 'tb_usuarios', user.uid);
        const userDoc = await getDoc(userDocRef);
        if (userDoc.exists()) {
          setCondominioId(userDoc.data().condominio_id);
        }
      }
    });
    return () => unsubscribe();
  }, []);

  // Busca a lista de funcionários para o filtro
  useEffect(() => {
    if (!condominioId) return;
    const q = query(collection(db, 'tb_funcionarios'), where('condominio_id', '==', condominioId), orderBy('nome', 'asc'));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const list = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setFuncionarios(list);
    });
    return () => unsubscribe();
  }, [condominioId]);

  // Busca os registros de ponto com base nos filtros
  useEffect(() => {
    if (!condominioId || !filtroFuncionario) {
      setRegistros([]);
      return;
    }

    let q = query(
      collection(db, 'tb_pontos'),
      where('condominio_id', '==', condominioId), // <-- CORREÇÃO: Adiciona o filtro de condomínio
      where('funcionario_id', '==', filtroFuncionario),
      orderBy('timestamp', 'desc')
    );
    
    // Aplica filtros de data se existirem
    if (filtroDataInicio) {
        q = query(q, where('timestamp', '>=', filtroDataInicio));
    }
    if (filtroDataFim) {
        q = query(q, where('timestamp', '<=', filtroDataFim));
    }

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const list = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setRegistros(list);
    }, (error) => {
      console.error("Erro ao buscar registros de ponto:", error);
      // Este erro pode ocorrer se o índice composto não existir no Firestore.
    });

    return () => unsubscribe();
  }, [condominioId, filtroFuncionario, filtroDataInicio, filtroDataFim]);

  const handleUpdateRegistro = async () => {
    if (!selectedRegistro || !editFormData.timestamp) return;
    const registroRef = doc(db, 'tb_pontos', selectedRegistro.id);
    try {
      await updateDoc(registroRef, {
        timestamp: editFormData.timestamp,
        comentario: editFormData.comentario,
      });
      setEditDialog(false);
      alert('Registro atualizado com sucesso!');
    } catch (error) {
      alert('Erro ao atualizar registro: ' + error.message);
    }
  };

  const handleDeleteRegistro = async (id) => {
    if (!window.confirm('Tem certeza que deseja deletar este registro?')) return;
    const registroRef = doc(db, 'tb_pontos', id);
    try {
      await deleteDoc(registroRef);
      alert('Registro deletado com sucesso.');
    } catch (error) {
      alert('Erro ao deletar registro: ' + error.message);
    }
  };

  const openEditDialog = (registro) => {
    setSelectedRegistro(registro);
    setEditFormData({
      timestamp: registro.timestamp.toDate(),
      comentario: registro.comentario || '',
    });
    setEditDialog(true);
  };

  const horasTrabalhadas = useMemo(() => {
    if (registros.length < 2) {
      return '00:00:00';
    }

    // Os registros vêm em ordem decrescente, então vamos invertê-los para a ordem cronológica
    const registrosAsc = [...registros].reverse();
    let totalMilliseconds = 0;
    let ultimaEntrada = null;

    for (const registro of registrosAsc) {
      if (registro.tipo === 'entrada') {
        // Armazena o timestamp da última entrada encontrada
        ultimaEntrada = registro.timestamp;
      } else if (registro.tipo === 'saida' && ultimaEntrada) {
        // Se encontrarmos uma saída e houver uma entrada anterior, calculamos a duração
        const saidaTime = registro.timestamp.toDate();
        const entradaTime = ultimaEntrada.toDate();
        totalMilliseconds += (saidaTime.getTime() - entradaTime.getTime());
        
        // Reseta a última entrada para que ela não seja usada novamente
        ultimaEntrada = null;
      }
    }

    if (totalMilliseconds <= 0) {
      return '00:00:00';
    }

    // Converte o total de milissegundos para um formato de duração
    const duration = intervalToDuration({ start: 0, end: totalMilliseconds });

    const hours = String(duration.hours || 0).padStart(2, '0');
    const minutes = String(duration.minutes || 0).padStart(2, '0');
    const seconds = String(duration.seconds || 0).padStart(2, '0');

    return `${hours}:${minutes}:${seconds}`;
  }, [registros]);

  const formatarDataHora = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = timestamp.toDate ? timestamp.toDate() : timestamp;
    return format(date, 'dd/MM/yyyy HH:mm:ss');
  };

  return (
    <Box>
      <Box component="main" sx={{ flexGrow: 1, p: 3, background: 'linear-gradient(45deg, #283593 30%, #1976d2 90%)', minHeight: '100vh' }}>
        <Typography variant="h4" gutterBottom sx={{ color: 'white' }}>Gerenciar Controle de Ponto</Typography>

        <Card className={styles.card}>
          <CardContent>
            <Typography variant="h5" className={styles.sectionTitle}>Filtros</Typography>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={4}>
                <FormControl fullWidth>
                  <InputLabel>Funcionário</InputLabel>
                  <Select value={filtroFuncionario} label="Funcionário" onChange={(e) => setFiltroFuncionario(e.target.value)}>
                    <MenuItem value=""><em>Selecione um funcionário</em></MenuItem> 
                    {funcionarios.map(f => <MenuItem key={f.id} value={f.user_uid}>{f.nome}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={3}><DatePicker label="Data Início" value={filtroDataInicio} onChange={setFiltroDataInicio} sx={{ width: '100%' }} /></Grid>
              <Grid item xs={12} md={3}><DatePicker label="Data Fim" value={filtroDataFim} onChange={setFiltroDataFim} sx={{ width: '100%' }} /></Grid>
              <Grid item xs={12} md={2}>
                <Button fullWidth onClick={() => { setFiltroDataInicio(null); setFiltroDataFim(null); }}>Limpar Datas</Button>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {filtroFuncionario && (
          <Card className={styles.card}>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h6" color="text.secondary">Total de Horas Trabalhadas no Período</Typography>
              <Typography variant="h4" component="div" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                {horasTrabalhadas}
              </Typography>
            </CardContent>
          </Card>
        )}

        {filtroFuncionario && (
          <Card className={styles.card}>
            <CardContent>
              <Typography variant="h5" className={styles.sectionTitle}>Registros</Typography>
              <List>
                {registros.map((registro) => (
                  <ListItem key={registro.id} divider secondaryAction={
                    <>
                      <IconButton edge="end" aria-label="edit" onClick={() => openEditDialog(registro)}><EditIcon /></IconButton>
                      <IconButton edge="end" aria-label="delete" onClick={() => handleDeleteRegistro(registro.id)} sx={{ ml: 1 }}><DeleteIcon /></IconButton>
                    </>
                  }>
                    <ListItemText
                      primary={`Tipo: ${registro.tipo.charAt(0).toUpperCase() + registro.tipo.slice(1)}`}
                      secondary={`Data e Hora: ${formatarDataHora(registro.timestamp)} ${registro.comentario ? `| Comentário: ${registro.comentario}` : ''}`}
                    />
                  </ListItem>
                ))}
                {registros.length === 0 && <Typography>Nenhum registro encontrado para os filtros selecionados.</Typography>}
              </List>
            </CardContent>
          </Card>
        )}
      </Box>

      {/* Modal de Edição */}
      <Dialog open={editDialog} onClose={() => setEditDialog(false)}>
        <DialogTitle>Editar Registro de Ponto</DialogTitle>
        <DialogContent>
          <DateTimePicker
            label="Data e Hora do Registro"
            value={editFormData.timestamp}
            onChange={(newValue) => setEditFormData({ ...editFormData, timestamp: newValue })}
            sx={{ width: '100%', mt: 2 }}
          />
          <TextField
            margin="dense"
            label="Comentário"
            type="text"
            fullWidth
            multiline
            rows={2}
            variant="standard"
            value={editFormData.comentario}
            onChange={(e) => setEditFormData({ ...editFormData, comentario: e.target.value })}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialog(false)}>Cancelar</Button>
          <Button onClick={handleUpdateRegistro}>Salvar</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default GerenciarPonto;
